import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { login as apiLogin, clearTokens, getAccessToken, perfilPropio } from '../lib';
import { getConductorByUserId } from '../lib/storage';
import type { Profile } from '../lib';

// ── Tipos ────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: number;
  username: string;
  email?: string;
  role: 'admin' | 'conductor' | 'vendedor' | 'unknown';
  profile?: Profile;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

// ── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ─────────────────────────────────────────────────────────────────

function resolveRole(profile?: Profile): 'admin' | 'conductor' | 'vendedor' | 'unknown' {
  // Superusuario siempre es admin
  if (profile?.is_superuser === 'true' || profile?.is_superuser === true as unknown as string) return 'admin';
  const rol = profile?.rol?.toLowerCase() ?? '';
  if (rol === 'admin' || rol === 'administrador') return 'admin';
  if (rol === 'conductor') return 'conductor';
  if (rol === 'vendedor') return 'vendedor';
  const modulos = profile?.modulos?.toLowerCase() ?? '';
  if (modulos.includes('conductor')) return 'conductor';
  if (modulos.includes('admin')) return 'admin';
  if (modulos.includes('vendedor')) return 'vendedor';
  return 'unknown';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Restaurar sesión al montar
  useEffect(() => {
    if (getAccessToken()) {
      loadProfile().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  async function loadProfile() {
    try {
      const res = await perfilPropio();
      const profile = res.data;
      const userId = Number(profile.user ?? 0);
      const roleFromProfile = resolveRole(profile);
      let role = roleFromProfile;

      // Solo consultamos el perfil de conductor cuando el rol del perfil es desconocido.
      if (roleFromProfile === 'unknown') {
        try {
          const conductor = userId > 0 ? await getConductorByUserId(userId) : undefined;
          if (conductor) {
            role = 'conductor';
          }
        } catch {
          role = roleFromProfile;
        }
      }

      setUser({
        id: userId,
        username: String(userId),
        role,
        profile,
      });
    } catch {
      clearTokens();
      setUser(null);
    }
  }

  async function login(username: string, password: string) {
    await apiLogin({ username, password });
    await loadProfile();
  }

  function logout() {
    clearTokens();
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
